import * as React from "react";
import {cx} from "emotion";

import "./informed-delivery.scss";

import {isApiTrue}      from "@Common/logic/is-api-true";
import {getCountryCode} from "@Common/logic/template-options/get-country-code";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {scnv}           from "@Common/react/state-change-nested-value";
import {UseState}       from "@Common/react/use-state";
import {cnv}            from "@Common/utils/arr-path";
import {chain}          from "@Common/utils/fs";
import {omit}           from "@Common/utils/objects";

import {ImageGalleryModal} from "../../../../image-gallery-modal/image-gallery-modal";
import {SliderCheckbox}    from "../../../../slider-checkbox/slider-checkbox";
import {groupEditors}      from "../../../common/group-editors";
import {RedirectURL}       from "../redirect-url/redirect-url";

import {EmailProof} from "./email-proof/email-proof";

export const InformedDelivery = (
    {
        itemId=undefined,
        agentId=undefined,
        initData,
        templateOptions,
        disabled=false,
        representativeImage=null,
        next,
    }
) => cs(
    consumeContext("company"),
    ["countryCode", (_, next) => next(getCountryCode({templateOptions}))],
    ["permanentlyDisabled", ({countryCode, company}, next) => next(
        !localStorage.DEV_ENABLE_INFORMED_DELIVERY && (
            countryCode !== "US" ||
            !isApiTrue(company?.prefs?.enableInformedDelivery) ||
            // (!initData?.representativeImage && !representativeImage) ||
            !(
                templateOptions?.product?.numberFolds <= 0 ||
                templateOptions?.enableIdWithNumberFolds
            ) ||
            false
        )
    )],
    ["disabled", ({permanentlyDisabled}, next) => next(
        !localStorage.DEV_ENABLE_INFORMED_DELIVERY &&(
            permanentlyDisabled ||
            disabled ||
            false
        )
    )],

    ["enabled", (_, next) => UseState({
        getInitValue: () => chain(
            initData?.enabled,
            (_) => ({
                saved: !!_,
                current: _ == null ? true : !!_,
            }),
        ),
        next,
    })],
    ["redirectURL", (_, next) => RedirectURL({
        itemId,
        initData,
        next,
    })],
    ["emailProof" , ({redirectURL}, next) => EmailProof({
        agentId,
        initData,
        learnMoreUrl: redirectURL.activeUrl,
        representativeImage,
        next,
    })],

    ["toggle", ({disabled, enabled}, next) => next(() => {
        if (disabled) {
            alert("Please select the mailing delivery method to proceed with this option");
        } else {
            scnv(enabled, ["current"], (_) => !_);
        }
    })],
    ({enabled, emailProof, disabled, redirectURL, permanentlyDisabled, toggle}) => next({

        editor: !enabled.value?.current || disabled ? {
            valid: true,

            $name: "Informed Delivery disabled",
            changed: !!enabled.value?.saved !== false,
            prepareSave: (data) => ({
                newValue: {
                    ...omit(data, ["redirectType","redirectUrl","displayName","rideAlongImage"]),
                    enabled: false,
                },
                onSaved: () => scnv(enabled, ["saved"], () => false),
            }),
        } : groupEditors([
            {
                valid: true,

                $name: "Informed Delivery enabled",
                changed: !!enabled.value?.saved !== true,
                prepareSave: (data) => ({
                    newValue: cnv(data, ["enabled"], () => true),
                    onSaved: () => scnv(enabled, ["saved"], () => true),
                }),
            },
            emailProof.editor,
            redirectURL.editor,
        ]),
        permanentlyDisabled,
        disabled,
        renderHeader: () => (
            <div className={cx("informed-delivery-1ko header", {disabled})}>
                <div className="left">
                    <div className="line1">
                        <span className="free-badge">FREE</span>
                        <img {...{
                            src: require("./logo.jpg"),
                            "xd-qa-type": "icon",
                            "xd-qa-name": "Informed Delivery",
                        }}/>

                        <div {...{
                            className: "text",
                            "xd-qa-type": "label",
                            "xd-qa-name": "Informed Delivery",
                        }}>
                            Informed Delivery by USPS <sup>&reg;</sup>
                        </div>

                        {cs(
                            ["imageGalleryModal", (_, next) => ImageGalleryModal({
                                hideThumbnails: true,
                                backgroundTransparency: .5,
                                maxWidth: 900,
                                next,
                            })],
                            ({imageGalleryModal}) => (
                                <a {...{
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        imageGalleryModal.show({
                                            imgs: [{enlarged: require("./InformedDelivery_Modal_R4.jpg")}],
                                        });
                                    },
                                    "xd-qa-type": "link",
                                    "xd-qa-name": "What is Informed Delivery",
                                }}>
                                    What is Informed Delivery?
                                </a>
                            )
                        )}
                    </div>

                    <div {...{
                        className: "sub-title",
                        "xd-qa-type": "text",
                        "xd-qa-name": "Informed Delivery Description",
                    }}>
                        <div className="line">
                            Informed Delivery offers you the ability to engage with your recipients through digital
                            marketing campaigns.
                        </div>
                        <div className="line">
                            Those enrolled will see the content below in an email from the United States Postal{" "}
                            Service, allowing them to preview and take action on incoming mailpieces. Available at 
                            no cost when you mail with Xpressdocs.
                        </div>
                    </div>
                </div>

                <div className="toggle-position">
                    {SliderCheckbox({
                        value: !disabled && !!enabled.value?.current,
                        onChange: toggle,
                        xdQaName: "Informed Delivery Toggle"
                    })}
                </div>
            </div>
        ),

        toggle,

        enabled: !disabled && !!enabled.value?.current,

        render: disabled || !enabled.value?.current ? null : (({showErrors}) => (
            <div {...{
                className: "informed-delivery-1ko content",
                "xd-qa-type": "card",
                "xd-qa-name": "Informed Delivery Panel",
            }}>
                <div className="box">
                    <div className="header">
                        <div {...{
                            className: "title",
                            "xd-qa-type": "label",
                            "xd-qa-name": "Email Image",
                        }}>
                            Email Image
                        </div>
                        <div {...{
                            className: "sub-title",
                            "xd-qa-type": "text",
                            "xd-qa-name": "Email Image Description",
                        }}>
                            The main image in the USPS Informed Delivery email will be the front side proof of your
                            mailed item.
                        </div>
                    </div>

                    <div className="content">
                        {emailProof.render({showErrors, templateOptions})}
                    </div>
                </div>

                <div className="box">
                    <div className="header">
                        <div {...{
                            className: "title",

                            "xd-qa-type": "label",
                            "xd-qa-name": "Redirect URL",
                        }}>
                            Redirect URL
                        </div>
                        <div {...{
                            className: "sub-title",
                            "xd-qa-type": "text",
                            "xd-qa-name": "Redirect URL Description",
                        }}>
                            This is the landing page your recipient will be redirected to after clicking the{" "}
                            Ride-Along Image.
                        </div>
                    </div>

                    <div className="content">
                        {redirectURL.render({showErrors})}
                    </div>
                </div>
            </div>
        )),
    }),
);

